import {
  CreateGMWEventTask,
  CreateGMWTask,
  EditGMWEventTask,
  EditGMWTask,
  QualityRegimeOptions,
} from "@nens/brostar/uploadapi-client";

import { BrostarUiSchema } from "@/libs/brostar-form";

import { createGmwUiSchemaLibrary } from "./gmw-ui-schema-library";

/**
 * Unified UI Schema hook for all GMW forms (events and full registration)
 * @param imbro - Quality regime selection
 * @returns Type-safe UI schema for the appropriate GMW form type
 */
export function useGmwUiSchema(
  qualityRegimeValue: QualityRegimeOptions | undefined
): BrostarUiSchema<EditGMWEventTask | CreateGMWEventTask | CreateGMWTask | EditGMWTask> {
  const {
    projectNumber,
    qualityRegime,
    deliveryAccountableParty,
    broId,
    correctionReason,
    registrationType,
    eventDate,
    wellStability,
    groundLevelStable,
    groundLevelPosition,
    groundLevelPositioningMethod,
    insertedPartLength,
    insertedPartDiameter,
    insertedPartMaterial,
    wellHeadProtector,
    maintenanceResponsibleParty,
    owner,
    objectIdAccountableParty,
    nitgCode,
    deliveryContext,
    constructionStandard,
    initialFunction,
    numberOfMonitoringTubes,
    wellConstructionDate,
    deliveredLocation,
    horizontalPositioningMethod,
    localVerticalReferencePoint,
    offset,
    verticalDatum,
    tubeNumber,
    tubeTopPosition,
    tubeTopPositioningMethod,
    variableDiameter,
    tubeTopDiameter,
    tubeMaterial,
    glue,
    isAbroad,
    geometricDataPubliclyAvailable,
    relatedSurveys,
    plainTubePartLength,
    tubeStatus,
    tubeType,
    artesianWellCapPresent,
    sedimentSumpPresent,
    numberOfGeoOhmCables,
    tubePackingMaterial,
    screenLength,
    sockMaterial,
    sedimentSumpLength,
    cableNumber,
    electrodeNumber,
    electrodePackingMaterial,
    electrodeStatus,
    electrodePosition,
  } = createGmwUiSchemaLibrary(qualityRegimeValue);

  // This schema works for both event and full forms because it includes all possible fields
  // RJSF will only render fields that exist in the actual schema
  const uiSchema: BrostarUiSchema<
    EditGMWEventTask | CreateGMWEventTask | CreateGMWTask | EditGMWTask
  > = {
    "ui:order": ["metadata", "registrationType", "sourcedocumentData"],
    metadata: {
      "ui:title": "Metadata",
      projectNumber,
      qualityRegime,
      deliveryAccountableParty,
      broId, // only on edit
      correctionReason,
    },
    registrationType,
    sourcedocumentData: {
      "ui:title": "Brondocumentgegevens",
      "ui:order": [
        // Event fields
        "eventDate",
        "wellHeadProtector",
        // GMW Start Registration fields
        "objectIdAccountableParty",
        "nitgCode",
        "deliveryContext",
        "constructionStandard",
        "initialFunction",
        "groundLevelStable",
        "owner",
        "maintenanceResponsibleParty",
        "wellConstructionDate",
        "deliveredLocation",
        "horizontalPositioningMethod",
        "localVerticalReferencePoint",
        "offset",
        "verticalDatum",
        "groundLevelPosition",
        "groundLevelPositioningMethod",
        "wellStability",
        "isAbroad",
        "geometricDataPubliclyAvailable",
        "relatedSurveys",
        // Insertion fields
        "insertedPartLength",
        "insertedPartDiameter",
        "insertedPartMaterial",
        // Single tube fields (for some events)
        "tubeNumber",
        "tubeTopPosition",
        "tubeTopPositioningMethod",
        // Array fields
        "numberOfMonitoringTubes",
        "monitoringTubes",
      ],
      // Event-specific fields
      eventDate,
      wellStability,
      groundLevelStable,
      groundLevelPosition,
      groundLevelPositioningMethod,
      insertedPartLength,
      insertedPartDiameter,
      insertedPartMaterial,
      tubeNumber,
      tubeTopPosition,
      tubeTopPositioningMethod,
      wellHeadProtector,
      maintenanceResponsibleParty,
      owner,
      isAbroad,
      geometricDataPubliclyAvailable,
      relatedSurveys,
      // GMW Start Registration fields
      objectIdAccountableParty,
      nitgCode,
      deliveryContext,
      constructionStandard,
      initialFunction,
      numberOfMonitoringTubes,
      wellConstructionDate,
      deliveredLocation,
      horizontalPositioningMethod,
      localVerticalReferencePoint,
      offset,
      verticalDatum,
      // Monitoring tubes (used by both event and full forms)
      // Using 'items' key for nested array configuration (RJSF v5 standard)
      monitoringTubes: {
        "ui:options": {
          labelField: "tubeNumber",
        },
        items: {
          "ui:order": [
            "tubeNumber",
            "tubeType",
            "artesianWellCapPresent",
            "sedimentSumpPresent",
            "tubeTopDiameter",
            "variableDiameter",
            "tubeStatus",
            "tubeTopPosition",
            "tubeTopPositioningMethod",
            "tubePackingMaterial",
            "tubeMaterial",
            "glue",
            "screenLength",
            "screenProtection",
            "sockMaterial",
            "plainTubePartLength",
            "sedimentSumpLength",
            "numberOfGeoOhmCables",
            "geoOhmCables",
          ],
          tubeNumber,
          tubeType,
          artesianWellCapPresent,
          sedimentSumpPresent,
          numberOfGeoOhmCables,
          tubeTopDiameter,
          variableDiameter,
          tubeStatus,
          tubeTopPosition,
          tubeTopPositioningMethod,
          tubePackingMaterial,
          tubeMaterial,
          glue,
          screenLength,
          sockMaterial,
          plainTubePartLength,
          sedimentSumpLength,
          geoOhmCables: {
            "ui:options": {
              labelField: "cableNumber",
            },
            items: {
              "ui:order": ["cableNumber", "electrodes"],
              cableNumber,
              electrodes: {
                "ui:options": {
                  labelField: "electrodeNumber",
                },
                items: {
                  "ui:order": [
                    "electrodeNumber",
                    "electrodePackingMaterial",
                    "electrodeStatus",
                    "electrodePosition",
                  ],
                  electrodeNumber,
                  electrodePackingMaterial,
                  electrodeStatus,
                  electrodePosition,
                },
              },
            },
          },
        },
      },
    },
  };

  return uiSchema;
}
